import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Award, 
  MessageSquare, 
  TrendingUp, 
  BookOpen,
  Mail,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowLeft,
  RefreshCw,
  ChevronRight,
  BarChart3,
  Lock,
  LogOut,
  Shield,
  Eye,
  EyeOff,
  Send,
  Bell
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { 
  adminLogin,
  adminVerify,
  adminLogout,
  getAdminDashboard, 
  getAdminUsers, 
  getAdminInquiries, 
  updateInquiryStatus,
  getAdminCertificates,
  getAdminEmails,
  sendProgressReminder,
  sendDeadlineAlert
} from '../services/api';
import { toast } from 'sonner';

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      await adminLogin(password);
      toast.success('Admin login successful');
      onLogin();
    } catch (err) {
      setError('Invalid admin password');
      toast.error('Invalid admin password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-white">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Admin Access</h1>
          <p className="text-slate-500 text-sm mt-1">WSafeTraining.com Dashboard</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Admin Password
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="pr-10"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          
          <Button 
            type="submit" 
            disabled={isLoading || !password}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="admin-login-button"
          >
            {isLoading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Lock className="w-4 h-4 mr-2" />
            )}
            {isLoading ? 'Logging in...' : 'Access Dashboard'}
          </Button>
        </form>
        
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-slate-500 hover:text-emerald-600">
            ← Back to Course
          </Link>
        </div>
      </Card>
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [emails, setEmails] = useState([]);
  const [emailStats, setEmailStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Check if admin is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { authenticated } = await adminVerify();
        setIsAuthenticated(authenticated);
        if (authenticated) {
          loadDashboardData();
        }
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [dashboardData, usersData, inquiriesData, certsData, emailsData] = await Promise.all([
        getAdminDashboard(),
        getAdminUsers(0, 10),
        getAdminInquiries(null, 0, 10),
        getAdminCertificates(0, 10),
        getAdminEmails(null, 0, 10)
      ]);
      
      setStats(dashboardData);
      setUsers(usersData.users || []);
      setInquiries(inquiriesData.inquiries || []);
      setCertificates(certsData.certificates || []);
      setEmails(emailsData.emails || []);
      setEmailStats(emailsData.stats_by_template || {});
    } catch (error) {
      console.error('Failed to load admin data:', error);
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
    loadDashboardData();
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
      setIsAuthenticated(false);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleUpdateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      await updateInquiryStatus(inquiryId, newStatus);
      toast.success(`Inquiry marked as ${newStatus}`);
      // Refresh inquiries
      const inquiriesData = await getAdminInquiries(null, 0, 10);
      setInquiries(inquiriesData.inquiries || []);
      // Refresh stats
      const dashboardData = await getAdminDashboard();
      setStats(dashboardData);
    } catch (error) {
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to update inquiry');
      }
    }
  };

  const handleSendProgressReminder = async (userId, userName) => {
    try {
      await sendProgressReminder(userId);
      toast.success(`Progress reminder sent to ${userName}`);
      // Refresh emails
      const emailsData = await getAdminEmails(null, 0, 10);
      setEmails(emailsData.emails || []);
      setEmailStats(emailsData.stats_by_template || {});
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const handleSendDeadlineAlert = async (userId, userName) => {
    try {
      await sendDeadlineAlert(userId, 7);
      toast.success(`Deadline alert sent to ${userName}`);
      // Refresh emails
      const emailsData = await getAdminEmails(null, 0, 10);
      setEmails(emailsData.emails || []);
      setEmailStats(emailsData.stats_by_template || {});
    } catch (error) {
      toast.error('Failed to send alert');
    }
  };

  // Loading state while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{label}</p>
          <p className="text-3xl font-bold text-slate-900">{value}</p>
          {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </Card>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      resolved: 'bg-green-100 text-green-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
        {status?.replace('_', ' ') || 'unknown'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Site</span>
              </Link>
              <div className="h-6 w-px bg-slate-300 hidden sm:block" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <h1 className="text-xl font-bold text-slate-900">Admin Dashboard</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadDashboardData}
                className="hidden sm:flex"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={Users} 
            label="Total Users" 
            value={stats?.total_users || 0} 
            color="bg-blue-500"
            subtext={`${stats?.recent_enrollments || 0} this week`}
          />
          <StatCard 
            icon={Award} 
            label="Certificates" 
            value={stats?.total_certificates || 0} 
            color="bg-purple-500"
          />
          <StatCard 
            icon={MessageSquare} 
            label="Support Inquiries" 
            value={stats?.total_inquiries || 0} 
            color="bg-orange-500"
            subtext={`${stats?.pending_inquiries || 0} pending`}
          />
          <StatCard 
            icon={Mail} 
            label="Newsletter Subs" 
            value={stats?.newsletter_subscribers || 0} 
            color="bg-emerald-500"
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <StatCard 
            icon={BookOpen} 
            label="Lesson Completions" 
            value={stats?.total_lesson_completions || 0} 
            color="bg-teal-500"
          />
          <StatCard 
            icon={TrendingUp} 
            label="Total Leads" 
            value={stats?.total_leads || 0} 
            color="bg-indigo-500"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200 overflow-x-auto">
          {['overview', 'users', 'inquiries', 'certificates', 'emails'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
                activeTab === tab
                  ? 'text-emerald-600 border-emerald-500'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Recent Users */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Recent Users
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setActiveTab('users')}>
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="space-y-3">
                {users.slice(0, 5).map((user, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                        {user.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{user.name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">{formatDate(user.created_at)}</span>
                  </div>
                ))}
                {users.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No users yet</p>
                )}
              </div>
            </Card>

            {/* Recent Inquiries */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-orange-500" />
                  Support Inquiries
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setActiveTab('inquiries')}>
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="space-y-3">
                {inquiries.slice(0, 5).map((inquiry, index) => (
                  <div key={index} className="py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-slate-900">{inquiry.name}</p>
                      {getStatusBadge(inquiry.status)}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-1">{inquiry.message}</p>
                  </div>
                ))}
                {inquiries.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No inquiries yet</p>
                )}
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'users' && (
          <Card className="p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              All Users
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                            {user.name?.charAt(0) || '?'}
                          </div>
                          <span className="font-medium text-slate-900">{user.name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{user.email}</td>
                      <td className="py-3 px-4 text-sm text-slate-500">{formatDate(user.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-8">No users found</p>
              )}
            </div>
          </Card>
        )}

        {activeTab === 'inquiries' && (
          <Card className="p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-orange-500" />
              Support Inquiries
            </h3>
            <div className="space-y-4">
              {inquiries.map((inquiry, index) => (
                <div key={index} className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-slate-900">{inquiry.name}</p>
                      <p className="text-sm text-slate-500">{inquiry.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(inquiry.status)}
                      <span className="text-xs text-slate-400">{formatDate(inquiry.created_at)}</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-3">{inquiry.message}</p>
                  <div className="flex gap-2">
                    {inquiry.status !== 'in_progress' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleUpdateInquiryStatus(inquiry.id, 'in_progress')}
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        In Progress
                      </Button>
                    )}
                    {inquiry.status !== 'resolved' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-green-600 hover:bg-green-50"
                        onClick={() => handleUpdateInquiryStatus(inquiry.id, 'resolved')}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {inquiries.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-8">No inquiries found</p>
              )}
            </div>
          </Card>
        )}

        {activeTab === 'certificates' && (
          <Card className="p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-500" />
              Issued Certificates
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Certificate #</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Recipient</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Issued</th>
                  </tr>
                </thead>
                <tbody>
                  {certificates.map((cert, index) => (
                    <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <span className="font-mono text-sm text-emerald-600">{cert.certificate_number}</span>
                      </td>
                      <td className="py-3 px-4 font-medium text-slate-900">{cert.lead_name}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{cert.lead_email}</td>
                      <td className="py-3 px-4 text-sm text-slate-500">{formatDate(cert.issued_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {certificates.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-8">No certificates issued yet</p>
              )}
            </div>
          </Card>
        )}

        {activeTab === 'emails' && (
          <div className="space-y-6">
            {/* Email Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-4 bg-blue-50 border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Send className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium text-blue-700">Welcome</span>
                </div>
                <p className="text-2xl font-bold text-blue-900">{emailStats.welcome || 0}</p>
              </Card>
              <Card className="p-4 bg-amber-50 border-amber-200">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium text-amber-700">Progress</span>
                </div>
                <p className="text-2xl font-bold text-amber-900">{emailStats.progress_reminder || 0}</p>
              </Card>
              <Card className="p-4 bg-red-50 border-red-200">
                <div className="flex items-center gap-2 mb-1">
                  <Bell className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-medium text-red-700">Deadline</span>
                </div>
                <p className="text-2xl font-bold text-red-900">{emailStats.deadline_alert || 0}</p>
              </Card>
              <Card className="p-4 bg-purple-50 border-purple-200">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-medium text-purple-700">Certificate</span>
                </div>
                <p className="text-2xl font-bold text-purple-900">{emailStats.certificate_earned || 0}</p>
              </Card>
            </div>

            {/* Send Email to Users */}
            <Card className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-500" />
                Send Emails to Users
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                              {user.name?.charAt(0) || '?'}
                            </div>
                            <span className="font-medium text-slate-900">{user.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{user.email}</td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleSendProgressReminder(user.user_id, user.name)}
                            >
                              <TrendingUp className="w-3 h-3 mr-1" />
                              Progress
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-amber-600 hover:bg-amber-50"
                              onClick={() => handleSendDeadlineAlert(user.user_id, user.name)}
                            >
                              <Bell className="w-3 h-3 mr-1" />
                              Deadline
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-8">No users found</p>
                )}
              </div>
            </Card>

            {/* Email Log */}
            <Card className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-500" />
                Email Log (Mock - Not Actually Sent)
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                These emails are logged to the database but not actually sent. Connect a real email service (SendGrid, Resend) to send them.
              </p>
              <div className="space-y-3">
                {emails.map((email, index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-slate-900">{email.subject}</p>
                        <p className="text-sm text-slate-500">To: {email.to_name} &lt;{email.to_email}&gt;</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          email.template === 'welcome' ? 'bg-blue-100 text-blue-700' :
                          email.template === 'progress_reminder' ? 'bg-amber-100 text-amber-700' :
                          email.template === 'deadline_alert' ? 'bg-red-100 text-red-700' :
                          email.template === 'certificate_earned' ? 'bg-purple-100 text-purple-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {email.template?.replace('_', ' ')}
                        </span>
                        <span className="text-xs text-slate-400">{formatDate(email.created_at)}</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600">{email.body_preview}</p>
                  </div>
                ))}
                {emails.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-8">No emails sent yet</p>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
